import {
  Breakpoints,
  Button,
  ButtonColor,
  ButtonSize,
  ButtonWidth,
  IconPhone,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { useRouter } from 'next/router';
import React, { type FC, useCallback } from 'react';

import { UserSourceType } from '../../__generated-gql-types__/globalTypes';
import { useAuth } from '../../context/AuthContext';
import mxpnl from '../../utils/analytics/mixpanel';
import phog from '../../utils/analytics/posthog/posthog';
import { TYPEFORM_URLS } from '../../utils/constants';
import { getUrlString } from '../../utils/url';

import { useTalkToTeamMemberUserQuery } from './__generated-gql-types__/TalkToATeamMemberButton.generated';

interface BaseTrackingVars {
  categorySlug: string;
  subCategorySlug?: string;
}

interface TalkToATeamMemberButtonProps {
  buttonId?: string;
  baseTrackingVars?: BaseTrackingVars;
  className?: string;
  buttonLabel?: string;
}

export const TalkToATeamMemberButton: FC<TalkToATeamMemberButtonProps> = ({
  baseTrackingVars,
  className = '',
  buttonId = 'Base',
  buttonLabel = 'Get help quick',
}) => {
  const { currentUser } = useAuth();
  const router = useRouter();
  const isMd = useMediaQuery(Breakpoints.MD);

  const href = getUrlString(
    TYPEFORM_URLS.TALK_TO_TEAM_MEMBER,
    {
      first_name: currentUser?.firstName,
      last_name: currentUser?.lastName,
      email: currentUser?.email,
      id: currentUser?.applicant?.id,
      referrer: router.asPath,
    },
    { useHash: true },
  );

  const onClickTalkToATeamMemberButton = useCallback(() => {
    mxpnl.track(
      `${buttonId} - click - Talk to a Team Member button`,
      baseTrackingVars,
    );
    phog.capture('talk_to_team_member_button--click', baseTrackingVars);
  }, [baseTrackingVars, buttonId]);

  const sourceType = currentUser?.source?.type;

  const isLinkedinSource =
    sourceType === UserSourceType.LINKEDIN ||
    sourceType === UserSourceType.LINKEDIN_TEST;

  const { data: userData, loading: userDataLoading } =
    useTalkToTeamMemberUserQuery();

  const blockIntro = userData?.user?.applicant?.blockIntroUntil;

  const hideGetHelpQuickButton =
    (isLinkedinSource && blockIntro) || userDataLoading;

  return (
    <div className={className}>
      {hideGetHelpQuickButton ? null : (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          id="talk_to_team_member--get_help_quick"
        >
          <Button
            label={buttonLabel}
            LeftIcon={IconPhone}
            buttonColor={ButtonColor.WHITE}
            size={isMd ? ButtonSize.MEDIUM : ButtonSize.LARGE}
            width={isMd ? ButtonWidth.AUTO : ButtonWidth.FULL}
            onClick={onClickTalkToATeamMemberButton}
          />
        </a>
      )}
    </div>
  );
};
